import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {Check, X} from "lucide-react";
import {get, isEmpty, map} from "lodash";
import React from "react";
import {useTranslation} from "react-i18next";
import {quizAnswerVerdict} from "business/quiz";
import {color, font, radius, shadow, type} from "common/tokens";
import {useSetTranslation} from "common/useSetTranslation";
import AnswerValue from "components/QuizAnswers/AnswerValue";

const useStyles = makeStyles({
  card: {
    background: color.SURFACE,
    border: `1px solid ${color.SURFACE_MUTED}`,
    borderRadius: radius.CARD,
    boxShadow: shadow.PANEL,
    boxSizing: 'border-box',
    padding: '24px 30px',
  },
  question: {
    ...type.sessionTitle,
    color: color.TEXT,
    margin: 0,
  },
  helpText: {
    ...type.body,
    fontSize: 16,
    fontWeight: font.MEDIUM,
    fontStyle: 'italic',
    color: color.TEXT,
    opacity: 0.5,
    margin: '10px 0 0',
  },
  label: {
    ...type.eyebrow,
    color: color.TEXT_MUTED,
    margin: '18px 0 8px',
  },
  block: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '14px 16px',
    borderRadius: radius.CONTROL,
    background: color.SURFACE_MUTED,
  },
  correct: {
    background: color.SUCCESS_TINT,
  },
  incorrect: {
    background: color.DANGER_TINT,
  },
  verdictIcon: {
    flexShrink: 0,
    display: 'flex',
    marginTop: 2,
  },
  correctIcon: {
    color: color.SUCCESS,
  },
  incorrectIcon: {
    color: color.DANGER,
  },
  value: {
    flex: 1,
    minWidth: 0,
  },
  option: {
    ...type.body,
    fontSize: 16,
    fontWeight: font.MEDIUM,
    color: color.TEXT,
    margin: 0,
  },
  unanswered: {
    ...type.body,
    fontStyle: 'italic',
    color: color.TEXT_SUBTLE,
    margin: 0,
  },
});

const SelfAnswerCard = ({index, question, quizAnswer}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();

  const label = st(question.title || question.label, get(question, 'localeTitle') || get(question, 'localeLabel'));
  const helpText = st(get(question, 'helpText'), get(question, 'localeHelpText'));
  const {graded, isCorrect, correct} = quizAnswerVerdict(question, quizAnswer);

  return (
    <div className={classes.card}>
      <p className={classes.question}>{`${t('questionPrefixQ')}${index}. ${label}`}</p>
      {!isEmpty(helpText) && <p className={classes.helpText}>{helpText}</p>}

      <p className={classes.label}>{t('yourAnswer')}</p>
      <div
        className={classNames(
          classes.block,
          graded && isCorrect && classes.correct,
          graded && !isCorrect && classes.incorrect
        )}
      >
        {graded && (
          <span
            className={classNames(
              classes.verdictIcon,
              isCorrect ? classes.correctIcon : classes.incorrectIcon
            )}
          >
            {isCorrect ? <Check size={18} strokeWidth={3}/> : <X size={18} strokeWidth={3}/>}
          </span>
        )}
        <span className={classes.value}>
          {quizAnswer
            ? <AnswerValue question={question} quizAnswer={quizAnswer}/>
            : <p className={classes.unanswered}>{t('notAnswered')}</p>}
        </span>
      </div>

      {graded && !isCorrect && (
        <>
          <p className={classes.label}>{t('correctAnswer')}</p>
          <div className={classNames(classes.block, classes.correct)}>
            <span className={classes.value}>
              {map(correct, option => (
                <p key={option.key} className={classes.option}>
                  {st(option.label, option.localeLabel)}
                </p>
              ))}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default SelfAnswerCard;
